import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import type {
  ActionDashboardItem,
  ResolutionConfigItemType,
} from '@frontend/api';
import {
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Part,
  PartLabel,
  PartThumbnail,
  PartThumbnailContainer,
  Text,
  UserGroupBadge,
  VStack,
} from '@frontend/legacy-components';
import { useDateFormat } from '@frontend/utils';

import { InteractiveFlex } from 'components/lib2/InteractiveContainers';

import { buildPopOutViewerUrl } from 'modules/quotes/utils/popOutViewer';
import {
  getResolutionIcon,
  getIsDestructive,
} from 'modules/rules/utils/resolutions';

import { getGroupPrimaryKey } from 'store/session/selectors';

import { useUserGroupBadgeLookup } from 'utils/multisite/hooks/useUserGroupBadge';
import { pluralize } from 'utils/stringUtils';

import ActionSwitchGroupPopover from './ActionSwitchGroupPopover';

const ResolutionPill = ({
  isDestructive = false,
  children,
}: {
  isDestructive?: boolean;
  children: ReactNode;
}) => (
  <Flex
    align="center"
    p={0.25}
    borderRadius="md"
    bg={isDestructive ? 'alert.surfaceError' : 'page.plateLight'}
  >
    {children}
  </Flex>
);

const ResolutionIcon = ({
  resolutionType,
}: {
  resolutionType: ResolutionConfigItemType;
}) => {
  const Icon = getResolutionIcon(resolutionType);
  const isDestructive = getIsDestructive(resolutionType);
  return (
    <ResolutionPill isDestructive={isDestructive}>
      <Icon
        boxSize={2.5}
        color={isDestructive ? 'interaction.destructive' : 'feature.secondary'}
      />
    </ResolutionPill>
  );
};

type ReqsReviewActionProps = {
  item: ActionDashboardItem;
};

export const ReqsReviewAction = ({ item }: ReqsReviewActionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isQuoteLinkPopoverOpen, setIsQuoteLinkPopoverOpen] = useState(false);

  const groupId = useSelector(getGroupPrimaryKey);
  const getUserGroupBadgeProps = useUserGroupBadgeLookup();
  const badgeProps = getUserGroupBadgeProps(item.groupId);

  const { generateRelativeDateTime } = useDateFormat();

  const { quoteLink, viewerLink } = useMemo(() => {
    const baseQuoteLink = `/quotes/edit/${item.component.quoteNumberWithRevision}/${item.component.quoteItemId}`;
    return {
      quoteLink: item.component.isRootComponent
        ? `${baseQuoteLink}?app=reqs-review`
        : `${baseQuoteLink}/${item.component.id}?app=reqs-review`,
      viewerLink: buildPopOutViewerUrl({
        quoteNumberRevisionNumber: item.component.quoteNumberWithRevision,
        quoteItemId: item.component.quoteItemId,
        activePartUuid: item.component.isRootComponent
          ? undefined
          : item.component.part.uuid,
        appsPanelState: 'reqs-review',
      }),
    };
  }, [item]);

  const isCurrentGroup = groupId === item.groupId;

  const relativeAssignedTime = item.assignedAt
    ? generateRelativeDateTime(item.assignedAt, true)
    : null;
  const assignedByName = item.assignedBy
    ? `${item.assignedBy.firstName} ${item.assignedBy.lastName}`
    : null;
  const isAssignedByUser = !item.assignedAutomatically && !!assignedByName;

  const handleQuoteLinkClick = (e: React.MouseEvent) => {
    if (!isCurrentGroup) {
      e.preventDefault();
      e.stopPropagation();
      setIsQuoteLinkPopoverOpen(true);
    }
  };

  const quoteLinkButton = isCurrentGroup ? (
    <Button as={Link} variant="link" size="sm" to={quoteLink} flexShrink={0}>
      Quote #{item.component.quoteNumberWithRevision}
    </Button>
  ) : (
    <ActionSwitchGroupPopover
      toggleOpen={setIsQuoteLinkPopoverOpen}
      isOpen={isQuoteLinkPopoverOpen}
      nextUrl={quoteLink}
      groupId={item.groupId}
    >
      <Button
        variant="link"
        size="sm"
        onClick={handleQuoteLinkClick}
        flexShrink={0}
      >
        Quote #{item.component.quoteNumberWithRevision}
      </Button>
    </ActionSwitchGroupPopover>
  );

  const cardContent = (
    <VStack align="stretch" gap={1} width="100%">
      <HStack gap={1} align="center" width="100%">
        <HStack
          flex="1 1 0"
          minWidth={0}
          gap={1}
          align="center"
          justify="flex-start"
        >
          <Part
            number={item.component.partNumber}
            filename={item.component.part.filename}
            rev={item.component.revision}
            size="xl"
            variant="borderless"
            minWidth={0}
          >
            <PartLabel
              display="flex"
              alignItems="baseline"
              minWidth={0}
              maxWidth="100%"
              sx={{
                '> .part__label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  wordBreak: 'normal',
                },
                '.part__label-group': {
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                },
              }}
            />
          </Part>
          {quoteLinkButton}
        </HStack>
        {badgeProps && (
          <Box
            flex="0 1 auto"
            maxWidth="50%"
            minWidth={0}
            display="flex"
            justifyContent="flex-end"
          >
            <UserGroupBadge
              maxWidth="100%"
              size="sm"
              name={badgeProps.name}
              colorPalette={badgeProps.colorPalette}
              icon={badgeProps.icon}
            />
          </Box>
        )}
      </HStack>

      <HStack gap={1.5} align="stretch" width="100%">
        <VStack flex={1} minWidth={0} align="start" gap={0.5}>
          <Heading size="md" as="h3">
            {item.rule.name}
          </Heading>

          {(item.rule.resolutionTypes.length > 0 || item.commentCount > 0) && (
            <Flex gap={1} align="center">
              {item.rule.resolutionTypes.map((resolutionType) => (
                <ResolutionIcon
                  key={resolutionType}
                  resolutionType={resolutionType}
                />
              ))}
              {item.commentCount > 0 && (
                <ResolutionPill>
                  <Text size="sm" color="feature.primary" px={0.5}>
                    {pluralize('message', item.commentCount)}
                  </Text>
                </ResolutionPill>
              )}
            </Flex>
          )}

          {relativeAssignedTime && (
            <Text
              size="sm"
              fontWeight="medium"
              color="feature.secondary"
              marginTop="auto"
            >
              {isAssignedByUser
                ? `Assigned by ${assignedByName} ${relativeAssignedTime}`
                : `Auto-assigned ${relativeAssignedTime}`}
            </Text>
          )}
        </VStack>

        <Part
          number={item.component.partNumber}
          filename={item.component.part.filename}
          rev={item.component.revision}
          isItar={item.component.isExportControlled}
          size="xl"
          variant="borderless"
          alignSelf="flex-start"
        >
          <PartThumbnailContainer>
            <PartThumbnail
              partFileUuid={item.component.part.primaryFileUuid}
              containsVirus={item.component.part.containsVirus}
              filename={item.component.part.filename}
              fileType={item.component.part.fileType}
              isAssembly={item.component.isAssembly}
              hasFailedChildren={item.component.part.hasFailedChildren}
              processingFailed={item.component.part.processingFailed}
            />
          </PartThumbnailContainer>
        </Part>
      </HStack>
    </VStack>
  );

  if (isCurrentGroup) {
    return (
      <Box
        as={Link}
        to={viewerLink}
        display="block"
        width="100%"
        p={1.5}
        backgroundColor="page.surface"
        color="feature.primary"
        _hover={{
          color: 'feature.primary',
          backgroundColor: 'interaction.surfaceHover',
        }}
        _active={{
          color: 'feature.primary',
          backgroundColor: 'interaction.surfaceActive',
        }}
        _notLast={{
          borderBottom: '1px solid',
          borderColor: 'separator.divider',
        }}
      >
        {cardContent}
      </Box>
    );
  }

  return (
    <ActionSwitchGroupPopover
      toggleOpen={setIsOpen}
      isOpen={isOpen}
      nextUrl={viewerLink}
      groupId={item.groupId}
    >
      <InteractiveFlex
        onClick={() => setIsOpen(true)}
        width="100%"
        p={1.5}
        color="feature.primary"
        _notLast={{
          borderBottom: '1px solid',
          borderColor: 'separator.divider',
        }}
      >
        {cardContent}
      </InteractiveFlex>
    </ActionSwitchGroupPopover>
  );
};
